/**
 * CategoryIcon — renders an icon for an action category (filter tag).
 *
 * Uses @untitledui/icons via the registry's domain-aliased exports, plus
 * the custom PipelineIcon for pipeline_update. A lookup table replaces the
 * ~200-line switch statement that previously held inline SVG duplicates.
 */

// @untitledui/icons for categories that don't have a domain alias
import { Archive, Edit03, SearchLg, Zap } from '@untitledui/icons';
import type { ComponentType } from 'react';

import { PipelineIcon } from '@/lib/crm/utils/icons/CustomIcons';

import type { FilterTag } from '../../lib/proposals/core/types';
import {
  CalendarIcon,
  CreateIcon,
  DeleteIcon,
  EmailIcon,
  ExternalLinkIcon,
  LinkIcon,
  MergeIcon,
  TeamIcon,
} from '../../lib/proposals/registry/icons';

type ActionCategory = FilterTag | 'all' | 'other';

/**
 * Lookup table mapping action categories to their icon component.
 *
 * Every icon here accepts `{ className?: string }` via the @untitledui rest
 * spread or the custom icon's explicit prop.
 */
const CATEGORY_ICONS: Record<
  ActionCategory,
  ComponentType<{ className?: string }>
> = {
  email: EmailIcon,
  automation: Zap,
  pipeline_update: PipelineIcon,
  property_update: Edit03,
  research: SearchLg,
  calendar: CalendarIcon,
  crm_create: CreateIcon,
  crm_delete: DeleteIcon,
  crm_associate: LinkIcon,
  crm_merge: MergeIcon,
  external_action: ExternalLinkIcon,
  onboarding: TeamIcon,
  all: Archive,
  other: Archive,
};

interface CategoryIconProps {
  category: ActionCategory;
  className?: string;
}

export default function CategoryIcon({
  category,
  className = 'w-6 h-6',
}: CategoryIconProps) {
  const Icon = CATEGORY_ICONS[category] ?? Archive;
  return <Icon className={className} />;
}
